import React, { memo, useEffect, useState, useSyncExternalStore } from 'react';
import { getClipThumbnailUrl } from '../Sessions/AssetsTable/AssetTableClipThumbnail/ClipPreviewUtils';
import ClipListItemInterimThumbnail from '../Clip/ClipInterimThumbnail/ClipListItemInterimThumbnail';
import SceneThumbnail from '../Clip/ClipPlayer/ClipNewTimeline/TimelineSegments/TimelineScenes/SceneThumbnail';
import { useSceneThumbnail } from '../TranscriptPage/useSceneThumbnail';
import { getAllCompositeSegments } from '../Clip/ClipPlayer/ClipNewTimeline/clipUpdateDataUtilsWithSplits/compositeUtils';
import { ClipRemotionThumbnail } from './ClipRemotionThumbnail';
import { isAudioContent } from '@/stores/core';
import Icon from '@/components/atoms/Icon';
import { classnames } from '@/libs/utils';
import { Clip } from '@/domains/asset';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';
import ErrorBoundaryForComponent from '@/App/ErrorBoundary/ErrorBoundaryForComponent';
import { CompositeSegment } from '@/App/remotion/MainPlayer/types/Segment/CompositeSegment';
import { useRemotionThumbnail } from '@/hooks/useRemotionThumbnail';
import { doesClipHavePlayerConfig } from '@/libs/clipUtils';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';

enum ClipListItemThumbnailType {
  Image = 'image',
  Interim = 'interim',
  Error = 'error'
}

interface ClipListItemThumbnailProps {
  clip: Clip;
  isClipActive?: boolean;
  isClipUnseen?: boolean;
  layout?: 'default' | 'fullWidth';
}

function ClipListItemThumbnail({ clip, isClipActive, isClipUnseen, layout = 'default' }: ClipListItemThumbnailProps) {
  const isAudio = isAudioContent();
  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const shouldDeleteConfig = !!featureFlags[FeatureFlagKeys.Use_CL_Delete_Config];
  const isConfigPresent = shouldDeleteConfig ? doesClipHavePlayerConfig(clip) : !!clip.asset_metadata.config;

  const [thumbnailType, setThumbnailType] = useState<ClipListItemThumbnailType>(
    isConfigPresent ? ClipListItemThumbnailType.Image : ClipListItemThumbnailType.Interim
  );

  const { canUseRemotionThumbnail } = useRemotionThumbnail({ clip });

  const [thumbnailUrl, setThumbnailUrl] = React.useState<string>(getClipThumbnailUrl(clip));
  const [isThumbnailChanging, setIsThumbnailChanging] = useState(false);

  const compositeSegment = getAllCompositeSegments(clip)[0];

  useEffect(() => {
    const eventListener = EventBus.on(CustomEvents.ThumbnailGenerated, res => {
      if (clip.asset_metadata.id === res.clip.asset_metadata.id) {
        setIsThumbnailChanging(true);
        setThumbnailUrl(getClipThumbnailUrl(clip));
        setThumbnailType(ClipListItemThumbnailType.Image);
      }
    });
    return () => {
      EventBus.off(CustomEvents.ThumbnailGenerated, eventListener);
    };
  }, [clip]);

  function onThumbnailLoaded() {
    setIsThumbnailChanging(false);
  }

  function onThumbnailLoadError() {
    setThumbnailType(ClipListItemThumbnailType.Error);
  }

  const { showSceneThumbnail, playerConfig, compositionHeight, compositionWidth, croppedImageUrl } = useSceneThumbnail({
    clip,
    startTimeInSeconds: clip.asset_metadata.start
  });

  if (canUseRemotionThumbnail) {
    return (
      <ClipRemotionThumbnail clip={clip} layout={layout} isClipUnseen={isClipUnseen} isClipActive={isClipActive} />
    );
  }

  return (
    <div
      className={classnames(
        'relative flex items-center justify-center overflow-hidden rounded-md bg-cover bg-center bg-no-repeat shadow-lg',
        {
          'aspect-square w-[62%]': clip.asset_metadata.size === 'SQUARE',
          'aspect-[9/16] w-[35%]': clip.asset_metadata.size === 'PORTRAIT',
          'aspect-video w-[85%]': clip.asset_metadata.size === 'LANDSCAPE' && layout === 'default',
          'aspect-video w-full !rounded-none': clip.asset_metadata.size === 'LANDSCAPE' && layout === 'fullWidth',
          '!border-none': isClipUnseen && !isClipActive,
          'h-full !shadow-none': layout === 'fullWidth'
        }
      )}
    >
      {showSceneThumbnail ? (
        <SceneThumbnail
          playerConfig={playerConfig}
          videoSize={clip.asset_metadata.size}
          thumbnailFrameToDisplay={0}
          compositeSegment={compositeSegment as CompositeSegment}
          compositionHeight={compositionHeight}
          compositionWidth={compositionWidth}
          parentWidth={'100%'}
          mediaContent={clip.content}
          croppedImageUrl={croppedImageUrl}
        />
      ) : thumbnailType === ClipListItemThumbnailType.Image ? (
        <div>
          <img
            data-testid="clip-preview-image"
            className={classnames('mx-auto transition-all duration-300', isThumbnailChanging ? 'blur-sm' : 'blur-0')}
            src={thumbnailUrl}
            alt="Preview"
            onLoad={onThumbnailLoaded}
            onError={onThumbnailLoadError}
          />
        </div>
      ) : thumbnailType === ClipListItemThumbnailType.Interim ? (
        <ErrorBoundaryForComponent onError={onThumbnailLoadError}>
          <ClipListItemInterimThumbnail clip={clip} />
        </ErrorBoundaryForComponent>
      ) : (
        <div className="flex size-6 items-center justify-center rounded-full border border-slate-300">
          <Icon name={isAudio ? 'IconHeadphones' : 'IconVideo'} size={13} stroke={2} className="text-slate-400/70" />
        </div>
      )}
    </div>
  );
}

export default memo(ClipListItemThumbnail);
